/**
 * <gnb-drawer-artist-menu> — builds the Artists branch of the mobile nav menu.
 *
 * Ported from the Hydrogen storefront's getStudioLinks(): the items are a fixed set of
 * paths under `{artist_studio_base_url}/{locale}`. They are assembled here rather than in
 * Liquid because the branch must only exist for artists, and Liquid cannot read the
 * user_type cookie (MIGRATION_NOTES.md #1 / #3). Anything Liquid renders ships to every
 * visitor; building the DOM here keeps the studio paths out of a non-artist's page.
 *
 * script-gnb-user-type.js hides the branch unless the cookie says ARTIST, so this element
 * only has to check whether it ended up inside a [hidden] ancestor. It is a display gate,
 * not an authorisation one — the studio itself still has to reject non-artists.
 *
 * Grouping matches Figma 639:54131: one unlabelled group of seven, a 1px rule, then
 * 아티스트 프로필 on its own. Row-major order fills the 2-column grid as drawn.
 *
 * The Artists row shown to everyone else points at `/landing`, which is what
 * LINK_STUDIO_LANDING was for in the original; that link is rendered in Liquid.
 */
const ARTIST_MENU_GROUPS = [
  ['dashboard', 'kit-album', 'mission', 'orders', 'settlement', 'analytics', 'analytics/fan-message'],
  ['profile/manage'],
];

/**
 * Labels live here rather than in the locale files for the same reason the markup does:
 * a Liquid-rendered JSON island would put them in every visitor's HTML. `en` is the
 * fallback for any storefront language without an entry.
 */
const ARTIST_MENU_LABELS = {
  ko: {
    dashboard: '대시보드',
    'kit-album': '키트앨범',
    mission: '아티스트 미션',
    orders: '주문',
    settlement: '정산',
    analytics: '팬 데이터',
    'analytics/fan-message': '팬 메시지',
    'profile/manage': '아티스트 프로필',
  },
  en: {
    dashboard: 'Dashboard',
    'kit-album': 'KiTalbum',
    mission: 'Artist Missions',
    orders: 'Order',
    settlement: 'Settlement',
    analytics: 'Fan Data',
    'analytics/fan-message': 'Artist Messages',
    'profile/manage': 'Artist Profile',
  },
};

class GnbDrawerArtistMenu extends HTMLElement {
  connectedCallback() {
    this.depth = this.closest('.gnb-drawer__depth');
    if (!this.depth || !this.dataset.prefix) {
      this.remove();
      return;
    }

    // user_type may still be resolving (script-gnb-user-type.js can be waiting on
    // /v1/web/user/info), so never give up on the first pass — wait to be told.
    this.onUserType = () => this.build();
    document.addEventListener('gnb:usertype', this.onUserType);
    this.build();
  }

  disconnectedCallback() {
    if (this.onUserType) document.removeEventListener('gnb:usertype', this.onUserType);
  }

  build() {
    // A hidden branch means this visitor is not an ARTIST — render nothing into the page.
    if (this.built || this.closest('[hidden]')) return;
    this.built = true;

    const labels = ARTIST_MENU_LABELS[this.dataset.locale] || ARTIST_MENU_LABELS.en;
    const prefix = this.dataset.prefix.replace(/\/+$/, '');
    const fragment = document.createDocumentFragment();

    ARTIST_MENU_GROUPS.forEach((paths, index) => {
      if (index > 0) {
        const rule = document.createElement('div');
        rule.className = 'gnb-drawer__rule';
        rule.setAttribute('role', 'presentation');
        fragment.appendChild(rule);
      }

      const section = document.createElement('div');
      section.className = 'gnb-drawer__section';

      const list = document.createElement('ul');
      list.className = 'gnb-drawer__grid';
      list.setAttribute('role', 'list');

      paths.forEach((path) => {
        const url = `${prefix}/${path}`;
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'gnb-drawer__leaf';
        link.href = url;
        link.textContent = labels[path] || ARTIST_MENU_LABELS.en[path] || path;
        if (this.isCurrent(url)) {
          link.classList.add('is-current');
          link.setAttribute('aria-current', 'page');
        }
        li.appendChild(link);
        list.appendChild(li);
      });

      section.appendChild(list);
      fragment.appendChild(section);
    });

    this.depth.replaceChildren(fragment);
  }

  isCurrent(url) {
    try {
      const target = new URL(url, window.location.href);
      return target.origin === window.location.origin && target.pathname === window.location.pathname;
    } catch (error) {
      return false;
    }
  }
}

customElements.define('gnb-drawer-artist-menu', GnbDrawerArtistMenu);
